import { useState } from "react";
import { ApiError } from "../api/client";
import { ProjectBilan } from "../components/ProjectBilan";
import { ProjectFormSheet } from "../components/ProjectFormSheet";
import { ProjectSources } from "../components/ProjectSources";
import { Tabs } from "../components/Tabs";
import { codeLabel } from "../lib/format";
import { ScreenHeader } from "../components/ScreenHeader";
import { useNavigate, useParams } from "react-router-dom";
import type { PmProject, PmWorkItem } from "../api/pm";
import {
  useProject,
  useProjectDashboard,
  useProjectMilestones,
  useProjectWorkItems,
} from "../hooks/usePm";
import { useAdminScope } from "../lib/auth/useGrantedScope";
import { useRole } from "../lib/auth/useRole";
import {
  chipMeta,
  MILESTONE_META,
  PROJECT_STATUS_META,
  WBS_STATUS_META,
} from "../lib/statusMeta";

// Onglets WBS + Jalons uniquement : Budget/RAID exclus de Chronos (DT §4bis.6).

function frDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", { timeZone: "UTC" });
}

/** Période et budget de la fiche, sur une ligne. Vide si rien n'est renseigné. */
function projectFacts(p: PmProject): string {
  const period =
    p.start_date && p.end_date
      ? `Du ${frDate(p.start_date)} au ${frDate(p.end_date)}`
      : p.start_date
        ? `Depuis le ${frDate(p.start_date)}`
        : p.end_date
          ? `Jusqu'au ${frDate(p.end_date)}`
          : "";
  const budget =
    p.budget_estimated !== undefined
      ? `Budget ${p.budget_estimated.toLocaleString("fr-FR")} ${p.budget_currency ?? ""}`.trim()
      : "";
  return [period, budget].filter(Boolean).join(" · ");
}

function wbsLevel(item: PmWorkItem): number {
  return item.path ? item.path.split(".").length - 1 : 0;
}

function WbsTab({ items }: { items: PmWorkItem[] }) {
  if (items.length === 0) {
    return <div className="empty-state">Aucun work item.</div>;
  }
  const sorted = [...items].sort((a, b) => a.path.localeCompare(b.path));
  return (
    <div className="card-list">
      {sorted.map((wi) => {
        const meta = chipMeta(WBS_STATUS_META, wi.status);
        return (
          <div
            key={wi.id}
            className="task-card"
            style={{ marginLeft: wbsLevel(wi) * 16, cursor: "default" }}
          >
            <div className="ttl">{wi.title}</div>
            <div className="row-line">
              <span
                className="chip"
                style={{ background: meta.bg, color: meta.color }}
              >
                {meta.label}
              </span>
              {wi.due_date && (
                <span className="due">
                  {new Date(wi.due_date).toLocaleDateString("fr-FR")}
                </span>
              )}
              <div className="progress-track">
                <div
                  className="fill"
                  style={{ width: `${wi.progress_pct}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ProjectDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const project = useProject(id);
  const workItems = useProjectWorkItems(id);
  const milestones = useProjectMilestones(id);
  const dashboard = useProjectDashboard(id);
  const [tab, setTab] = useState<"wbs" | "jalons">("wbs");
  const [editing, setEditing] = useState(false);
  const canEdit = useAdminScope("pm:project:update");
  const admin = useRole() === "admin";

  const p = project.data;
  const status =
    project.error instanceof ApiError ? project.error.status : undefined;

  return (
    <div className="screen project-detail-screen">
      <ScreenHeader
        title={p ? codeLabel(p.code, p.name) : "Projet"}
        lead={
          <button
            className="btn ghost"
            aria-label="Retour aux projets"
            onClick={() => navigate("/projets")}
          >
            ←
          </button>
        }
        aside={
          canEdit &&
          p && (
            <button
              type="button"
              className="pill-btn"
              onClick={() => setEditing(true)}
            >
              Modifier
            </button>
          )
        }
      />

      {project.isLoading && <div className="empty-state">Chargement…</div>}

      {project.isError && (
        <div className="empty-state">
          {status === 403
            ? "Vous n'êtes pas membre de ce projet."
            : status === 404
              ? "Projet introuvable."
              : "Impossible de charger le projet."}
        </div>
      )}

      {editing && p && (
        <ProjectFormSheet
          project={p}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      {p && (
        <div className="card-list">
          <div className="task-card" style={{ cursor: "default" }}>
            <div className="row-line">
              <span
                className="chip"
                style={{
                  background: chipMeta(PROJECT_STATUS_META, p.status).bg,
                  color: chipMeta(PROJECT_STATUS_META, p.status).color,
                }}
              >
                {chipMeta(PROJECT_STATUS_META, p.status).label}
              </span>
              <div className="progress-track">
                <div className="fill" style={{ width: `${p.progress_pct}%` }} />
              </div>
              <span className="due">{p.progress_pct}%</span>
            </div>
            {dashboard.data && (
              <div className="meta-line">
                {dashboard.data.work_items.done}/
                {dashboard.data.work_items.total} work items terminés ·{" "}
                {dashboard.data.work_items.overdue} en retard ·{" "}
                {dashboard.data.milestones.validated}/
                {dashboard.data.milestones.total} jalons validés
              </div>
            )}
            {projectFacts(p) && (
              <div className="meta-line">{projectFacts(p)}</div>
            )}
            {p.description && <div className="meta-line">{p.description}</div>}
          </div>
        </div>
      )}

      {p && admin && <ProjectSources projectId={p.id} projectName={p.name} />}

      {p && <ProjectBilan projectId={p.id} />}

      {p && (
        <Tabs
          className="seg-block seg-inset"
          label="Vues du projet"
          value={tab}
          onChange={setTab}
          items={[
            { id: "wbs", label: "WBS" },
            { id: "jalons", label: "Jalons" },
          ]}
        />
      )}

      {p &&
        tab === "wbs" &&
        (workItems.isLoading ? (
          <div className="empty-state">Chargement…</div>
        ) : (
          <WbsTab items={workItems.data?.items ?? []} />
        ))}

      {p &&
        tab === "jalons" &&
        (milestones.isLoading ? (
          <div className="empty-state">Chargement…</div>
        ) : (
          <div className="card-list">
            {(milestones.data?.items ?? []).map((m) => {
              // Statuts Go : pending | in_review | validated | missed → design done/late/upcoming.
              // target_date = minuit UTC (colonne DATE) : comparer au début du
              // jour UTC, pas à l'instant courant, sinon un jalon dû aujourd'hui
              // serait « En retard » toute la journée (le backend ne le marque
              // missed que si target_date < today, deadline_checker.go).
              const now = new Date();
              const todayUtc = Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
              );
              const late =
                m.status === "missed" ||
                (m.status !== "validated" &&
                  new Date(m.target_date).getTime() < todayUtc);
              const key =
                m.status === "validated" ? "done" : late ? "late" : "upcoming";
              const meta = chipMeta(MILESTONE_META, key);
              return (
                <div
                  key={m.id}
                  className="task-card"
                  style={{ cursor: "default" }}
                >
                  <div className="ttl">{m.name}</div>
                  <div className="row-line">
                    <span
                      className="chip"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                    <span className="due">
                      {new Date(m.target_date).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                </div>
              );
            })}
            {(milestones.data?.items ?? []).length === 0 && (
              <div className="empty-state">Aucun jalon.</div>
            )}
          </div>
        ))}
    </div>
  );
}

export default ProjectDetailScreen;
